import { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClient } from '../api';
import { LoadingSpinner } from '../components';
import { useToastContext } from '../contexts/ToastContext';
import '../styles/BillingPage.css';
import { RAZORPAY_API_KEY } from '../utils/constants';

const BillingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const [billingData, setBillingData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  // Removed userDetails state as billing information is no longer collected

  // Log when coupon is applied
  useEffect(() => {
    if (appliedCoupon) {
      console.log('Coupon applied successfully!', appliedCoupon);
      console.log('Discount amount:', discount);
    }
  }, [appliedCoupon, discount]);


  useEffect(() => {
    // Get billing data from navigation state
    if (location.state) {
      setBillingData(location.state);
    } else {
      // Redirect back if no billing data
      toast.error('No billing information found. Please select items first.');
      navigate('/dashboard');
    }
  }, [location.state, navigate]);

  // Removed form validation as billing information is no longer collected

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      addToast('Please enter a coupon code', 'error');
      return;
    }

    setIsValidatingCoupon(true);
    try {
      const client = await apiClient.initialize();
      const params=new URLSearchParams();
      params.append('code',couponCode.trim());
      if(billingData.courseId){
        params.append('courseId',billingData.courseId);
      }
      if(billingData.selectedTopics.length>0){
        params.append('topicIds',billingData.selectedTopics.map(topic=>topic.id).join(','));
      }

        const response = await client.post(`/coupons/validate-coupon?${params.toString()}`,
        null,
       {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

      if (response.status === 200 && response.data) {
        const finalAmount = parseFloat(response.data.data);
        if (isNaN(finalAmount)) {
          throw new Error('Invalid amount received from server');
        }
        
        const discountAmount = billingData.totalAmount - finalAmount;
        setDiscount(discountAmount);
        
        const newCoupon = {
          code: couponCode,
          discount: discountAmount,
          type: response.data.discountType || 'flat'
        };
        
        setAppliedCoupon(newCoupon);
        addToast(response.data.message || 'Coupon applied successfully!', 'success');
        
        // Update the billing data with the new final amount
        setBillingData(prev => ({
          ...prev,
          totalAmount: billingData.totalAmount
        }));
      } else {
        const errorMessage = response.data?.message || 'Invalid or expired coupon code';
        console.error('Coupon validation failed:', errorMessage);
        addToast(errorMessage, 'error');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Coupon validation failed:', error);
      addToast(error.response?.data?.message || 'Failed to validate coupon', 'error');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setDiscount(0);
    setAppliedCoupon(null);
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      // Calculate final amount after applying discount
      const finalAmount = Math.max(0, billingData.totalAmount - (discount || 0));
      
      console.log('Payment amount calculation:', {
        totalAmount: billingData.totalAmount,
        discount: discount || 0,
        finalAmount: finalAmount
      });
      
      // Initialize API client
      const client = await apiClient.initialize();

      // 📌 RAZORPAY PAYMENT FLOW:
      // 1️⃣ Backend creates order using Razorpay API (/orders endpoint)
      // 2️⃣ Frontend uses order_id to initialize Razorpay checkout
      // 3️⃣ After payment, Razorpay returns: payment_id, order_id, signature
      // 4️⃣ Backend verifies signature using HMAC SHA256 with key_secret

      // Validate amount and discount
      if (isNaN(finalAmount) || finalAmount <= 0) {
        throw new Error('Invalid payment amount. Please try again.');
      }

      if (appliedCoupon && (isNaN(discount) || discount <= 0)) {
        throw new Error('Invalid discount amount. Please try applying the coupon again.');
      }

      // Get current user from auth store
      const authStorage = localStorage.getItem('auth-storage');
      let userId = null;
      
      try {
        if (authStorage) {
          const { state } = JSON.parse(authStorage);
          userId = state?.currentUser?.id || state?.currentUser?.userId;
          console.log('Current user ID from auth store:', userId);
        }
      } catch (error) {
        console.error('Error getting user from auth store:', error);
      }

      if (!userId) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Prepare payment order payload
      const orderPayload = {
        amount: finalAmount,
        userId: userId, // Include the authenticated user's ID
        isFullCourse: billingData.isFullCourse // Explicitly indicate if this is a full course purchase
      };

      // Always include courseId in the payload when available (for both full course and selected topics)
      if (!billingData.courseId) {
        console.error('Missing courseId in billing data');
        throw new Error('Course information is missing. Please try again.');
      }
      orderPayload.courseId = billingData.courseId;
      
      // For topic-specific purchases, include the selected topic IDs as an array
      if (!billingData.isFullCourse && billingData.selectedTopics?.length > 0) {
        // Convert all selected topic IDs to strings and validate them
        const topicIds = billingData.selectedTopics.map(topic => {
          const topicId = String(topic.id || '').trim();
          if (!topicId) {
            console.error('Invalid topic ID found in selection:', topic.id);
            throw new Error('One or more selected topics have invalid IDs. Please try again.');
          }
          return topicId;
        });
        
        orderPayload.topicId = topicIds;
        console.log('Purchasing topics with IDs:', topicIds);
      } else if (billingData.isFullCourse) {
        // For full course purchase, we don't need to specify topicId
        console.log('Purchasing full course - no specific topic ID needed');
      } else {
        console.error('No topics selected for purchase');
        throw new Error('No topics selected for purchase');
      }

      // Add coupon code if applicable
      if (appliedCoupon) {
        orderPayload.couponCode = appliedCoupon.code;
        // Removed discountAmount and discountType as they're not part of CreateOrderRequest
      }

      console.log('Creating payment order with payload:', JSON.stringify(orderPayload, null, 2));

      // Step 1: Create payment order with final amount after discount
      let createOrderResponse;
      try {
        createOrderResponse = await client.post('/payment/create-order', orderPayload);
        
        if (!createOrderResponse?.data) {
          throw new Error('Invalid response from payment server');
        }
      } catch (error) {
        console.error('Error creating payment order:', {
          error: error.response?.data || error.message,
          status: error.response?.status,
          headers: error.response?.headers,
          config: error.config
        });
        
        const errorMessage = error.response?.data?.message || 
                           error.message || 
                           'Failed to create payment order. Please try again.';
        throw new Error(errorMessage);
      }

      // Extract order details from backend response
      // Backend returns: { message: "Order Created Successfully.", statusCode: 201, data: { order_id: "order_xyz", amount: 4999, currency: "INR" } }
      console.log('Backend order creation response:', createOrderResponse.data);
      console.log('Keys in response data:', Object.keys(createOrderResponse.data.data || {}));
      console.log('Full data object:', createOrderResponse.data.data);
      const { order_id: razorpayOrderId, amount, currency } = createOrderResponse.data.data;
      console.log('Extracted order details:', { razorpayOrderId, amount, currency });

      // Step 2: Initialize Razorpay payment
      console.log('Order ID being passed to Razorpay:', razorpayOrderId);
      const options = {
        key: RAZORPAY_API_KEY,
        amount: amount, // Amount in paise (e.g., 50000 for ₹500) - This is the final amount after discount
        currency: currency || 'INR',
        name: 'Somayya Academy',
        description: billingData.isFullCourse
          ? `Complete Course - ${billingData.courseTitle}`
          : `Selected Topics - ${billingData.selectedTopics.length} topic(s)`,
        order_id: razorpayOrderId, // This is the order ID from Razorpay API
        modal: {
          ondismiss: function () {
            console.log('Payment modal dismissed');
          }
        },
        // Removed prefill as billing information is no longer collected
        theme: {
          color: '#22c55e'
        },
        // Replace your existing handler function with this fixed version:

        handler: async function (response) {
          try {
            // Step 3: Verify payment
            console.log('Payment completed. Razorpay response:', response);
            console.log('Full response object:', JSON.stringify(response, null, 2));

            // Debug: Check if all required fields are present
            if (!response.razorpay_payment_id) {
              console.error('Missing razorpay_payment_id');
              throw new Error('Payment ID not received from Razorpay');
            }
            if (!response.razorpay_order_id) {
              console.error('Missing razorpay_order_id');
              throw new Error('Order ID not received from Razorpay');
            }
            if (!response.razorpay_signature) {
              console.error('Missing razorpay_signature');
              throw new Error('Signature not received from Razorpay');
            }

            const verifyPayload = {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              ...(appliedCoupon && {
                couponCode: appliedCoupon.code,
                discountAmount: appliedCoupon.discount,
                discountType: appliedCoupon.type
              })
            };

            // Always include courseId and topicIds in the payload
            verifyPayload.courseId = billingData.courseId;
            console.log('Adding courseId:', billingData.courseId);
            
            // For full course purchase, include all topic IDs if available in billingData
            if (billingData.isFullCourse) {
              // If topics are available in billingData, use them, otherwise an empty array
              verifyPayload.topicIds = billingData.topics?.map(topic => topic.id) || [];
              console.log('Adding all topicIds for full course:', verifyPayload.topicIds);
            } else {
              // For individual topic purchase, include selected topic IDs
              verifyPayload.topicIds = billingData.selectedTopics?.map(topic => topic.id) || [];
              console.log('Adding selected topicIds:', verifyPayload.topicIds);
            }

            console.log('Final verify payload:', JSON.stringify(verifyPayload, null, 2));

            // Verify payment with backend
            const verifyResponse = await apiClient.post('/payment/verify', verifyPayload);
            console.log('Payment verification successful:', verifyResponse);
            
            // Prepare payment data
            const paymentData = {
              ...billingData,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              paymentStatus: 'success',
              paymentDate: new Date().toISOString(),
              purchasedTopicIds: verifyPayload.topicIds
            };
            
            console.log('Payment processed:', paymentData);
            
            // Show success message and update UI
            toast.success('Payment successful! Your purchase is complete. Redirecting to My Courses...');
            setIsProcessing(false);
            
            // Navigate to My Courses page
            navigate('/my-courses', { 
              state: { 
                paymentSuccess: true,
                paymentData: paymentData
              },
              replace: true // Replace current entry in history
            });

            console.log('Payment processed:', paymentData);
            
            // Create a success message based on what was purchased
            let successMessage = 'Payment successful! ';
            if (billingData.isFullCourse) {
              successMessage += `You now have full access to the course: ${billingData.courseTitle}`;
            } else {
              successMessage += `You now have access to ${billingData.selectedTopics.length} topic(s) from ${billingData.courseTitle}`;
            }
            
            toast.success(successMessage);

            // Navigate to success page or dashboard with purchased items
            
          } catch (error) {
            console.error('Payment verification failed:', error);
            console.error('Error details:', error.response?.data);
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
      };

      // Check if Razorpay is loaded
      if (typeof window.Razorpay === 'undefined') {
        throw new Error('Razorpay SDK not loaded. Please refresh the page and try again.');
      }

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!billingData) {
    return (
      <div className="billing-loading">
        <LoadingSpinner />
        <p>Loading billing information...</p>
      </div>
    );
  }

  return (
    <div className="billing-container">
      <div className="billing-header">
        <button className="back-button" onClick={handleBack}>
          ← Back
        </button>
        <h1 className="billing-title">Complete Your Purchase</h1>
      </div>

      <div className="billing-content">
        {/* Order Summary */}
        <div className="order-summary">
          <h2 className="section-title">Order Summary</h2>

          <div className="course-info">
            <h3 className="course-title">{billingData.courseTitle}</h3>

            {billingData.isFullCourse ? (
              <div className="purchase-item">
                <div className="item-info">
                  <span className="item-name">Complete Course</span>
                  <span className="item-desc">
                    Access to all {billingData.selectedTopics.length} topics
                  </span>
                </div>
                <div className="item-price">₹{billingData.totalAmount}</div>
              </div>
            ) : (
              <div className="selected-topics">
                <div className="topics-header">
                  <span>Selected Topics ({billingData.selectedTopics.length})</span>
                </div>
                {billingData.selectedTopics.map((topic, index) => (
                  <div key={topic.id} className="topic-item">
                    <span className="topic-number">Chapter {index + 1}</span>
                    <span className="topic-name">{topic.title}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="price-breakdown">
              {billingData.courseOriginalPrice &&
                billingData.courseOriginalPrice !== billingData.totalAmount && (
                  <div className="price-row">
                    <span>Original Price:</span>
                    <span className="original-price">₹{billingData.courseOriginalPrice}</span>
                  </div>
                )}
              <div className="price-row total">
                <span>Total Amount:</span>
                <span className="total-price">₹{billingData.totalAmount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="payment-section">
          <h2 className="section-title">Payment</h2>
          <div className="payment-info">
            <p>Complete your purchase securely with Razorpay.</p>
            <p className="payment-note">
              Your payment information is secure and encrypted.
            </p>
            
            <div className="coupon-section">
              {appliedCoupon ? (
                <div className="coupon-applied">
                  <span>Coupon {appliedCoupon.code} applied! (-₹{appliedCoupon.discount})</span>
                  <button 
                    type="button" 
                    className="remove-coupon"
                    onClick={removeCoupon}
                    disabled={isProcessing}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="coupon-input-container">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter coupon code"
                    className="coupon-input"
                    disabled={isProcessing || isValidatingCoupon}
                  />
                  <button
                    type="button"
                    className="validate-coupon"
                    onClick={validateCoupon}
                    disabled={!couponCode.trim() || isProcessing || isValidatingCoupon}
                  >
                    {isValidatingCoupon ? 'Validating...' : 'Apply'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="final-amount">
            <span>Amount to Pay:</span>
            <span className="amount">₹{Math.max(0, billingData.totalAmount - discount)}</span>
            {discount > 0 && (
              <span className="discount-text">You save ₹{discount}!</span>
            )}
          </div>

          <button
            className="pay-button"
            onClick={handlePayment}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <LoadingSpinner size="small" />
                Processing Payment...
              </>
            ) : (
              `Pay ₹${Math.max(0, billingData.totalAmount - discount)}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;